import { Fragment } from 'react';
import { MdExpandMore, MdInfoOutline } from 'react-icons/md';

import { ButtonOutlined } from '@/components/Button';
import { DeclinedBadge } from '@/components/DeclinedBadge/DeclinedBadge';
import { PendingBadge } from '@/components/PendingBadge/PendingBadge';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { formatRole } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget.constants';
import type { InviteEmailTemplate } from '@/store/auth/auth.dto';
import { extraGuestsTag, partyHeadcount, sumHeadcount } from '@/store/events/events.dto';
import type { PastGuest } from '@/store/events/events.mappers';
import type { GuestInvitee } from '@/store/invites/invites.store';
import { isOwnerGroupRole } from '@/utils/roles';

import { AUDIENCE_OPTIONS } from '../eventWizard.constants';
import type { CrewMember, EventGuest, Guest } from '../eventWizard.types';
import s from '../EventWizardModal.module.scss';

import { InvitePanel } from './InvitePanel';

interface StepAudienceProps {
  crewMembers: CrewMember[];
  /** Owners and crew who will be on the event; the rest are left out of it. */
  attendingIds: string[];
  /** On create, the backend seats every owner and crew member but those left out. */
  creating: boolean;
  /** Someone who cannot be left out — the creator, on create. */
  lockedId: string | null;
  onToggleTeamMember: (id: string) => void;
  onAddAllTeam: () => void;
  teamOpen: boolean;
  onToggleTeamOpen: () => void;
  guestsOpen: boolean;
  onToggleGuestsOpen: () => void;
  guests: Guest[];
  onRemoveGuest: (id: number) => void;
  /** People who were guests at this vessel's past events — invited by person id. */
  pastGuests: PastGuest[];
  pastGuestsSelected: string[];
  onTogglePastGuest: (personId: string) => void;
  onClearInvite: () => void;
  inviteName: string;
  onInviteNameChange: (value: string) => void;
  inviteContact: string;
  onInviteContactChange: (value: string) => void;
  onSendInvitation: (picked: GuestInvitee[]) => void;
  /** The guests already on the event being edited, answered or not. Empty when creating. */
  eventGuests: EventGuest[];
  inviteExpiresAt: string | null;
  onInviteExpiresAtChange: (value: string) => void;
  inviteEmailTemplate: InviteEmailTemplate;
  onInviteEmailTemplateChange: (value: InviteEmailTemplate) => void;
  inviteNote: string;
  onInviteNoteChange: (value: string) => void;
  /** The event's end: the latest expiry an invitation may be given. */
  eventEndsAt: Date | null;
  /**
   * Owners and crew on leave over the event's days, each with what to say about it — `On leave:
   * Apr 9 – Apr 11, 2027`. They stay pickable, listed last in their group with the label.
   */
  away?: ReadonlyMap<string, string>;
}

export function StepAudience({
  crewMembers,
  attendingIds,
  creating,
  lockedId,
  onToggleTeamMember,
  onAddAllTeam,
  teamOpen,
  onToggleTeamOpen,
  guestsOpen,
  onToggleGuestsOpen,
  guests,
  onRemoveGuest,
  pastGuests,
  pastGuestsSelected,
  onTogglePastGuest,
  onClearInvite,
  inviteName,
  onInviteNameChange,
  inviteContact,
  onInviteContactChange,
  onSendInvitation,
  eventGuests,
  inviteExpiresAt,
  onInviteExpiresAtChange,
  inviteEmailTemplate,
  onInviteEmailTemplateChange,
  inviteNote,
  onInviteNoteChange,
  eventEndsAt,
  away,
}: StepAudienceProps) {
  // Whoever is away goes last; the sort is stable, so each group keeps its own order.
  const awayLast = (members: CrewMember[]) =>
    away?.size
      ? [...members].sort((a, b) => Number(away.has(a.id)) - Number(away.has(b.id)))
      : members;
  // Owners and co-owners are seated like the crew, but they are not crew: they host the
  // event, while the crew works it. Neither is invited — only guests are.
  const owners = awayLast(crewMembers.filter((m) => isOwnerGroupRole(m.role)));
  const crew = awayLast(crewMembers.filter((m) => !isOwnerGroupRole(m.role)));

  const renderMember = (m: CrewMember) => {
    const attending = attendingIds.includes(m.id);
    const locked = m.id === lockedId;
    const awayLabel = away?.get(m.id);
    const awayPill = awayLabel && (
      <StatusPill label={awayLabel} tone="progress" wrap className={s.personAway} />
    );
    const content = (
      <>
        <div className={s.personAvatar}>{m.name.charAt(0)}</div>
        {locked ? (
          // The backend always seats whoever creates the event (not necessarily its
          // organiser) — say why their row cannot be tapped.
          <div className={s.personInfo}>
            <span className={s.personName}>{m.name} (you)</span>
            <span className={s.personRole}>
              You&apos;re the creator of this event, so you&apos;re always on it
            </span>
            {awayPill}
          </div>
        ) : awayPill ? (
          <div className={s.personInfo}>
            <span className={s.personName}>{m.name}</span>
            {awayPill}
          </div>
        ) : (
          <span className={s.personName}>{m.name}</span>
        )}
        <span className={s.personRole}>{formatRole(m.role)}</span>
        <span className={s.personToggle}>{attending ? '✓' : '+'}</span>
      </>
    );
    if (locked) {
      return (
        <div key={m.id} className={`${s.personRow} ${s.personRowSelected}`}>
          {content}
        </div>
      );
    }
    return (
      <button
        key={m.id}
        type="button"
        className={`${s.personRow} ${s.personRowSelectable} ${attending ? s.personRowSelected : ''}`}
        onClick={() => onToggleTeamMember(m.id)}
        aria-pressed={attending}
      >
        {content}
      </button>
    );
  };

  const crewCount = crewMembers.filter((m) => attendingIds.includes(m.id)).length;
  const eventGuestsPeople = sumHeadcount(eventGuests, partyHeadcount);
  const guestListPeople = sumHeadcount(guests, partyHeadcount);
  const selectedPastCount = pastGuests.filter((g) =>
    pastGuestsSelected.includes(g.personId),
  ).length;
  // One person, one invitation: whichever list picked them first locks them out of the other.
  const inGuestList = guests.flatMap((g) => g.personId ?? []);
  const unavailable = Object.fromEntries([
    ...pastGuestsSelected.map((id) => [id, 'Selected from recent events']),
    ...inGuestList.map((id) => [id, 'Already in guest list']),
  ]);

  return (
    <>
      <div className={s.stepHeader}>
        <h3 className={s.stepTitle}>Who is this event for?</h3>
        <p className={s.stepSubtitle}>
          {creating
            ? 'The owners and crew join automatically. You can invite guests too.'
            : 'Please add or remove owners and crew, or invite guests.'}
        </p>
      </div>
      <div className={s.fields}>
        {AUDIENCE_OPTIONS.map((opt) => {
          const open = opt.id === 'team' ? teamOpen : guestsOpen;
          const toggle = opt.id === 'team' ? onToggleTeamOpen : onToggleGuestsOpen;
          const count =
            opt.id === 'team' ? crewCount : eventGuests.length + guests.length + selectedPastCount;
          return (
            <div key={opt.id} className={s.audienceCard}>
              <button
                type="button"
                className={s.audienceHeader}
                onClick={toggle}
                aria-expanded={open}
              >
                <span className={s.optionEmoji}>{opt.emoji}</span>
                <span className={s.audienceHeaderText}>
                  <span className={s.optionLabel}>{opt.label}</span>
                  <span className={s.optionDesc}>{opt.desc}</span>
                </span>
                {count > 0 && <span className={s.audienceCount}>{count}</span>}
                <MdExpandMore
                  className={`${s.audienceChevron} ${open ? s.audienceChevronOpen : ''}`}
                />
              </button>

              {open && opt.id === 'team' && (
                <div className={s.audienceBody}>
                  {creating && (
                    <div className={s.crewAutoNote}>
                      <MdInfoOutline />
                      The owners and every crew member join this event automatically. Please tap
                      anyone who won’t be there. Guests join only when you invite them.
                    </div>
                  )}
                  <div className={s.crewHeader}>
                    <div className={s.subSectionTitle}>{crewCount} on this event</div>
                    <ButtonOutlined
                      onClick={onAddAllTeam}
                      disabled={crewCount === crewMembers.length}
                    >
                      + Add all owners &amp; crew
                    </ButtonOutlined>
                  </div>
                  {crewMembers.length === 0 && (
                    <span className={s.personRole}>
                      Nobody’s aboard yet. Please add crew to the vessel first.
                    </span>
                  )}
                  {[
                    { title: 'Owners', members: owners },
                    { title: 'Crew', members: crew },
                  ].map(
                    (group) =>
                      group.members.length > 0 && (
                        <Fragment key={group.title}>
                          <div className={s.subSectionTitle}>
                            {group.title} ({group.members.length})
                          </div>
                          <div className={s.rowList}>{group.members.map(renderMember)}</div>
                        </Fragment>
                      ),
                  )}
                </div>
              )}

              {open && opt.id === 'guests' && (
                <div className={s.audienceBody}>
                  <InvitePanel
                    name={inviteName}
                    onNameChange={onInviteNameChange}
                    contact={inviteContact}
                    onContactChange={onInviteContactChange}
                    onClear={onClearInvite}
                    onSend={onSendInvitation}
                    unavailable={unavailable}
                    expiresAt={inviteExpiresAt}
                    onExpiresAtChange={onInviteExpiresAtChange}
                    emailTemplate={inviteEmailTemplate}
                    onEmailTemplateChange={onInviteEmailTemplateChange}
                    note={inviteNote}
                    onNoteChange={onInviteNoteChange}
                    eventEndsAt={eventEndsAt}
                  />

                  {eventGuests.length > 0 && (
                    <>
                      <div className={s.subSectionTitle}>
                        On this event{' '}
                        <span className={s.listCount}>
                          ({eventGuests.length})
                          {eventGuestsPeople > eventGuests.length &&
                            ` · ${eventGuestsPeople} people`}
                        </span>
                      </div>
                      <div className={s.rowList}>
                        {eventGuests.map((guest) => (
                          <div key={guest.key} className={s.personRow}>
                            <div className={s.personAvatar}>
                              {guest.name.charAt(0).toUpperCase()}
                            </div>
                            <span className={s.personName}>
                              {guest.name}
                              {extraGuestsTag(guest.additionalGuests)}
                            </span>
                            {guest.status === 'accepted' && (
                              <span className={s.personRole}>Accepted</span>
                            )}
                            {guest.status === 'declined' && (
                              <DeclinedBadge reason={guest.declineReason} />
                            )}
                            {guest.status === 'invited' && <PendingBadge />}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {pastGuests.length > 0 && (
                    <>
                      <div className={s.subSectionTitle}>
                        From recent events ({selectedPastCount} selected)
                      </div>
                      <div className={s.rowList}>
                        {pastGuests.map((g) => {
                          const selected = pastGuestsSelected.includes(g.personId);
                          const locked = inGuestList.includes(g.personId);
                          return (
                            <button
                              key={g.personId}
                              type="button"
                              className={`${s.personRow} ${s.personRowSelectable} ${selected ? s.personRowSelected : ''} ${locked ? s.personRowLocked : ''}`}
                              onClick={() => onTogglePastGuest(g.personId)}
                              aria-pressed={selected}
                              disabled={locked}
                            >
                              <div className={s.personAvatar}>{g.name.charAt(0).toUpperCase()}</div>
                              <span className={s.personName}>
                                {g.name}
                                {extraGuestsTag(g.additionalGuests)}
                              </span>
                              <span className={s.personRole}>{g.lastEventTitle}</span>
                              {locked ? (
                                <span className={s.personLockedPill}>In guest list</span>
                              ) : (
                                <span className={s.personToggle}>{selected ? '✓' : '+'}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* The heading names the list, so it sits on it and waits for the first guest. */}
                  {guests.length > 0 && (
                    <>
                      <div className={s.subSectionTitle}>
                        Guest list{' '}
                        <span className={s.listCount}>
                          ({guests.length})
                          {guestListPeople > guests.length && ` · ${guestListPeople} people`}
                        </span>
                      </div>
                      <div className={s.rowList}>
                        {guests.map((guest) => (
                          <div key={guest.id} className={s.personRow}>
                            <div className={s.personAvatar}>
                              {guest.name.charAt(0).toUpperCase()}
                            </div>
                            <div className={s.personInfo}>
                              <span className={s.personName}>
                                {guest.name}
                                {extraGuestsTag(guest.additionalGuests)}
                              </span>
                              <span className={s.personRole}>{guest.contact}</span>
                            </div>
                            <PendingBadge />
                            <button
                              type="button"
                              className={s.removeBtn}
                              onClick={() => onRemoveGuest(guest.id)}
                              aria-label={`Remove ${guest.name}`}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
